import { createDefaultDoc } from '../model/defaults';
import type { IdGen } from '../model/ids';
import { parseDoc } from '../model/schema';
import type { KinklistDoc } from '../model/types';
import type { StorageLike } from './storage';
import { readItem, writeItem } from './storage';

export const DOC_KEY = 'kinklist:doc:v3';
/** Unreadable saved data is moved here instead of being thrown away. */
export const CORRUPT_DOC_KEY = 'kinklist:doc:v3:unreadable';

export type LoadStatus =
  /** Nothing saved yet: the default list. */
  | 'fresh'
  /** The saved list. */
  | 'restored'
  /** Saved data was unreadable; started over (the data is kept under `CORRUPT_DOC_KEY`). */
  | 'recovered';

export interface LoadResult {
  doc: KinklistDoc;
  status: LoadStatus;
}

export function loadDoc(
  storage: StorageLike | null,
  ctx: { newId: IdGen; now: () => Date },
): LoadResult {
  const raw = readItem(storage, DOC_KEY);
  if (raw === null) return { doc: createDefaultDoc(ctx.newId, ctx.now()), status: 'fresh' };
  let doc: KinklistDoc | null;
  try {
    doc = parseDoc(JSON.parse(raw));
  } catch {
    doc = null;
  }
  if (doc) return { doc, status: 'restored' };
  writeItem(storage, CORRUPT_DOC_KEY, raw);
  return { doc: createDefaultDoc(ctx.newId, ctx.now()), status: 'recovered' };
}

export function saveDoc(storage: StorageLike | null, doc: KinklistDoc): boolean {
  return writeItem(storage, DOC_KEY, JSON.stringify(doc));
}

export interface Timers {
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
}

const defaultTimers: Timers = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (handle) => {
    globalThis.clearTimeout(handle as ReturnType<typeof globalThis.setTimeout>);
  },
};

export interface Debounced {
  /** (Re)starts the timer. */
  schedule(): void;
  /** Runs now if something is pending (e.g. on `pagehide`). */
  flush(): void;
  cancel(): void;
  readonly pending: boolean;
}

export function debounce(
  fn: () => void,
  delayMs: number,
  timers: Timers = defaultTimers,
): Debounced {
  let handle: unknown = null;
  const clear = () => {
    if (handle !== null) timers.clearTimeout(handle);
    handle = null;
  };
  return {
    schedule() {
      clear();
      handle = timers.setTimeout(() => {
        handle = null;
        fn();
      }, delayMs);
    },
    flush() {
      if (handle === null) return;
      clear();
      fn();
    },
    cancel: clear,
    get pending() {
      return handle !== null;
    },
  };
}
